import { Modal, StyleSheet, TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { router } from "expo-router"
import { usePostHog } from "posthog-react-native"

import { Text } from "src/components/Ignite/Text"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"

interface LoginOrRequestAccessModalProps {
  visible: boolean
  onDismiss: () => void
  /** The channel's subscription URL — when null, only the Login button is shown */
  subscriptionUrl: string | null
  channelId: string
}

/**
 * Modal shown when an unauthenticated user taps a subscription-only item,
 * or presses Talk/Type. Matches the spec on slides 7, 11, and 12.
 *
 * - "Login" → navigates to /unauth/login (preserving channelId in MMKV via ChannelLinkCapture)
 * - "Request Access" → opens subscriptionUrl in browser (only shown when url is non-null)
 * - X → dismisses
 */
export function LoginOrRequestAccessModal({
  visible,
  onDismiss,
  subscriptionUrl,
  channelId,
}: LoginOrRequestAccessModalProps) {
  const { themed, theme } = useAppTheme()
  const posthog = usePostHog()

  const handleLogin = () => {
    onDismiss()
    router.replace("/unauth/login")
  }

  const handleRequestAccess = () => {
    posthog?.capture("unauth_request_access_clicked", { channelId })
    if (subscriptionUrl) {
      openLinkInBrowser(subscriptionUrl)
    }
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={onDismiss}
        accessibilityLabel="Close"
      >
        <View style={styles.backdropInner}>
          <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()}>
            <View style={themed($sheet)}>
              {/* Close button */}
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onDismiss}
                accessibilityRole="button"
                accessibilityLabel="Close"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <MaterialIcons name="close" size={22} color={theme.colors.text} />
              </TouchableOpacity>

              {/* Content */}
              <Text style={themed($heading)}>Account Required.</Text>
              <Text style={themed($body)}>
                Interactions with a Sidekick about this content require an account.
              </Text>

              {/* Buttons */}
              <View style={styles.buttonRow}>
                <TouchableOpacity
                  style={themed($loginButton)}
                  onPress={handleLogin}
                  accessibilityRole="button"
                  accessibilityLabel="Login"
                >
                  <Text style={themed($loginButtonText)}>Login</Text>
                </TouchableOpacity>

                {subscriptionUrl ? (
                  <TouchableOpacity
                    style={themed($requestButton)}
                    onPress={handleRequestAccess}
                    accessibilityRole="button"
                    accessibilityLabel="Request Access"
                  >
                    <Text style={themed($requestButtonText)}>Request Access</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  backdropInner: {
    width: "100%",
    maxWidth: 380,
  },
  closeButton: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 10,
    padding: 4,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
})

const $sheet: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  backgroundColor: colors.background,
  borderRadius: 16,
  padding: spacing.lg,
  paddingTop: spacing.xl,
  width: "100%",
  gap: spacing.md,
})

const $heading: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 18,
  fontFamily: typography.primary.bold,
  color: colors.text,
})

const $body: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  lineHeight: 20,
})

const $loginButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.tint ?? colors.primary,
  borderRadius: 8,
  paddingVertical: 12,
  alignItems: "center",
})

const $loginButtonText: ThemedStyle<TextStyle> = ({ typography }) => ({
  fontSize: 15,
  fontFamily: typography.primary.medium,
  color: "#fff",
})

const $requestButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.avatarControlButtonBackground ?? "#444",
  borderRadius: 8,
  paddingVertical: 12,
  alignItems: "center",
})

const $requestButtonText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 15,
  fontFamily: typography.primary.medium,
  color: colors.text,
})
